import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import CollectionCard from "@/components/feature/CollectionCard";
import usePageMeta from "@/hooks/usePageMeta";
import { hotEvents, mustSee, categories } from "@/mocks/guagua";

const ALL = [...hotEvents, ...mustSee];

export default function Collections() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCat = searchParams.get("cat") ?? "all";
  const urlQuery = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(urlQuery);

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "网爆吃瓜合集导航",
        url: "https://yoovx.cn/collections",
        description: "网爆吃瓜合集导航页，按分类与关键词筛选娱乐话题合集，支持一周盘点与必看清单。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://yoovx.cn/" },
          { "@type": "ListItem", position: 2, name: "合集导航", item: "https://yoovx.cn/collections" },
        ],
      },
    ],
    []
  );

  usePageMeta({
    title: "网爆吃瓜合集导航 - 精选列表与汇总帖分类筛选",
    description:
      "网爆吃瓜合集导航页支持分类筛选与关键词搜索，按综艺、剧组、音乐现场、红毯时尚、粉丝互动、品牌动态六大方向整理精选列表与汇总帖，快速定位想看的合集。",
    keywords: "网爆吃瓜合集导航,网爆吃瓜精选列表,网爆吃瓜汇总帖,网爆吃瓜分类,娱乐话题合集",
    path: "/collections",
    jsonLd,
  });

  const filtered = useMemo(() => {
    const q = urlQuery.trim().toLowerCase();
    return ALL.filter((item) => {
      const matchCat = activeCat === "all" || item.categoryId === activeCat;
      if (!matchCat) return false;
      if (!q) return true;
      const haystack = `${item.title} ${item.summary} ${item.categoryName} ${item.tags.join(" ")}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [activeCat, urlQuery]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (keyword.trim()) next.set("q", keyword.trim());
    else next.delete("q");
    setSearchParams(next);
  };

  const handleCat = (catId: string) => {
    const next = new URLSearchParams(searchParams);
    if (catId === "all") next.delete("cat");
    else next.set("cat", catId);
    setSearchParams(next);
  };

  const activeCatName = categories.find((c) => c.id === activeCat)?.name;

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="w-full px-4 py-10 md:px-8 md:py-14">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-700">
                首页
              </Link>
              <i className="ri-arrow-right-s-line leading-none" aria-hidden="true"></i>
              <span className="text-foreground-800">合集导航</span>
              {activeCatName && (
                <>
                  <i className="ri-arrow-right-s-line leading-none" aria-hidden="true"></i>
                  <span className="text-primary-700">{activeCatName}</span>
                </>
              )}
            </nav>

            <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-5xl">
              网爆吃瓜合集导航
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              全站合集总入口。按主题分类浏览，或用关键词搜索话题，所有合集都整理好了背景、时间线与看点清单，点进去就能一次看完。
            </p>

            <form onSubmit={handleSubmit} className="mt-7 flex w-full max-w-xl flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <i
                  className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400 leading-none"
                  aria-hidden="true"
                ></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索合集标题、标签或话题关键词"
                  aria-label="搜索合集"
                  className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-300/60"
                />
              </div>
              <button
                type="submit"
                className="h-12 shrink-0 rounded-md bg-primary-500 px-7 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <div className="mt-6 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleCat("all")}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  activeCat === "all"
                    ? "bg-foreground-950 text-background-50"
                    : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                全部合集
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCat(cat.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeCat === cat.id
                      ? "bg-foreground-950 text-background-50"
                      : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-col gap-2 border-b border-background-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-600">
              共找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 个合集
              {urlQuery && (
                <>
                  ，关键词「<span className="text-foreground-900">{urlQuery}</span>」
                </>
              )}
            </p>
            {(urlQuery || activeCat !== "all") && (
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams());
                }}
                className="inline-flex items-center gap-1 self-start text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
              >
                <i className="ri-refresh-line leading-none" aria-hidden="true"></i>
                重置筛选
              </button>
            )}
          </div>

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((item) => (
                <CollectionCard key={item.id} item={item} />
              ))}
            </div>
          ) : (
            <div className="mt-12 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-100/50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-emotion-sad-line text-3xl leading-none" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">暂时没有匹配的合集</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-600">
                换个关键词试试，或者直接浏览全部合集。新的合集每周都会补充进来。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setSearchParams(new URLSearchParams());
                }}
                className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部合集
              </button>
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}